import { Fragment, useContext } from 'react';
import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import {
  ArticleDeleteArticleActionCell,
  ArticleIssueCell,
  ArticleNameCell,
  ArticlePrintArticleSheetActionCell,
  ArticleReceivedVerificationActionCell,
  ArticleReceivedVerificationStatusCell,
  ArticleRequestCardItem,
  ArticleRequestCell,
  ArticleWeightCardItem,
  ArticleWeightCell,
} from '@/components/ArticlesTableCells';
import { ArticleShipmentNextTaskCell } from '@/components/ArticlesTableCells/ArticleShipmentNextTaskCell';
import { Card, CardActions, CardContent, CardItems } from '@/components/Card/Card';
import Badge from '@/design_system/Badge';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useArticleName } from '@/models/article';
import { ShipmentWithRelations } from '@/models/shipment';
import { ShipmentArticlesContext } from '@/routes/Shipments/Shipment/components/ShipmentArticles/ShipmentArticles';

type ShipmentArticle = ShipmentWithRelations['articles'][number];

export const ArticlesTable = ({ shipmentArticles }: { shipmentArticles: ShipmentArticle[] }) => {
  const { t } = useLingui();
  const {
    showVerificationStatus,
    showVerificationActions,
    showPrintArticleSheetActions,
    showDeleteArticleAction,
    showNextTaskColumn,
  } = useContext(ShipmentArticlesContext);

  const columnWidths = {
    items: 'minmax(150px, 3fr)',
    status: showVerificationStatus && 'minmax(150px, 2fr)',
    request: 'minmax(200px, 2fr)',
    weight: 'auto',
    actions:
      showVerificationActions || showPrintArticleSheetActions || showDeleteArticleAction
        ? 'minmax(120px, 1fr)'
        : showNextTaskColumn
          ? 'minmax(180px, 2fr)'
          : undefined,
  };

  return (
    <Table
      ariaLabel={t({ id: 'shipment.articles.table.label', message: 'Items' })}
      columnWidths={[
        columnWidths['items'],
        columnWidths['status'],
        columnWidths['request'],
        columnWidths['weight'],
        columnWidths['actions'],
      ]}
    >
      <ArticlesTableHeader />
      <Body>
        {shipmentArticles.map((shipmentArticle) => (
          <Fragment key={shipmentArticle.articleId}>
            <ArticlesTableRow shipmentArticle={shipmentArticle} />
            {shipmentArticle.issue && <ArticleIssueRow shipmentArticle={shipmentArticle} />}
          </Fragment>
        ))}
      </Body>
    </Table>
  );
};

const ArticlesTableHeader = () => {
  const { t } = useLingui();
  const { showVerificationStatus, showActionsColumn } = useContext(ShipmentArticlesContext);

  return (
    <Header>
      <Row>
        <Column>
          <Trans id="shipment.articles.table.articles.label">Items</Trans>
        </Column>
        {showVerificationStatus && (
          <Column>
            <Trans id="shipment.articles.table.status.label">Status</Trans>
          </Column>
        )}
        <Column>
          <Trans id="shipment.articles.table.request.label">Request</Trans>
        </Column>
        <Column>
          <Trans id="shipment.articles.table.weight.label">Weight</Trans>
        </Column>
        {showActionsColumn && (
          <Column
            ariaLabel={t({
              id: 'shipment.articles.table.actions.label',
              message: 'Actions',
            })}
          />
        )}
      </Row>
    </Header>
  );
};

const ArticlesTableRow = ({ shipmentArticle }: { shipmentArticle: ShipmentArticle }) => {
  const {
    showVerificationStatus,
    showVerificationActions,
    showPrintArticleSheetActions,
    showDeleteArticleAction,
    showActionsColumn,
    showNextTaskColumn,
    shipment,
  } = useContext(ShipmentArticlesContext);

  const article = shipmentArticle.article;

  return (
    <Row style={{ opacity: article.archivedAt ? '0.5' : undefined }}>
      <Cell>
        <ArticleNameCell
          article={article}
          id={article.id}
          badge={
            article.archivedAt ? (
              <Badge size="small" color="danger">
                <Trans id="shipment.articles.table.archived.badge">Archived</Trans>
              </Badge>
            ) : null
          }
        />
      </Cell>
      {showVerificationStatus && (
        <Cell>
          <ArticleReceivedVerificationStatusCell shipmentArticle={shipmentArticle} />
        </Cell>
      )}
      <Cell>
        <ArticleRequestCell article={article} organization={shipment.organization} />
      </Cell>
      <Cell>
        <ArticleWeightCell article={article} />
      </Cell>

      {showActionsColumn && (
        <Cell justifyContent={showNextTaskColumn ? 'center' : 'end'}>
          {showVerificationActions && (
            <ArticleReceivedVerificationActionCell
              shipmentArticle={shipmentArticle}
              shipment={shipment}
              key={`${shipmentArticle.verified}`}
            />
          )}

          {(showPrintArticleSheetActions || showDeleteArticleAction) && (
            <Stack row gap="0.5rem">
              {showPrintArticleSheetActions && (
                <ArticlePrintArticleSheetActionCell article={article} />
              )}
              {showDeleteArticleAction && (
                <ArticleDeleteArticleActionCell
                  articleId={article.id}
                  shipmentId={shipment.id}
                  isDisabled={shipment.articles.length === 1}
                />
              )}
            </Stack>
          )}

          {showNextTaskColumn && <ArticleShipmentNextTaskCell article={article} />}
        </Cell>
      )}
    </Row>
  );
};

const ArticleIssueRow = ({ shipmentArticle }: { shipmentArticle: ShipmentArticle }) => {
  return (
    <Row isExtensionRow>
      <Cell isWholeRow>
        <ArticleIssueCell issue={shipmentArticle.issue ?? ''} />
      </Cell>
    </Row>
  );
};

export const ArticlesCardList = ({ shipmentArticles }: { shipmentArticles: ShipmentArticle[] }) => {
  const { t } = useLingui();

  return (
    <GridList
      aria-label={t({
        id: 'shipment.articles.table.label',
        message: 'Items',
      })}
    >
      {shipmentArticles.map((shipmentArticle) => (
        <ArticleCard key={shipmentArticle.articleId} shipmentArticle={shipmentArticle} />
      ))}
    </GridList>
  );
};

const ArticleCard = ({ shipmentArticle }: { shipmentArticle: ShipmentArticle }) => {
  const {
    showVerificationStatus,
    showVerificationActions,
    showPrintArticleSheetActions,
    showDeleteArticleAction,
    showNextTaskColumn,
    showActionsColumn,
    shipment,
  } = useContext(ShipmentArticlesContext);
  const articleName = useArticleName({ article: shipmentArticle.article });

  const article = shipmentArticle.article;

  return (
    <GridListItem
      textValue={articleName}
      style={{ marginBottom: '0.5rem', opacity: article.archivedAt ? '0.5' : undefined }}
    >
      <Card>
        <CardContent>
          <ArticleNameCell
            id={article.id}
            article={article}
            badge={
              <>
                {article.archivedAt ? (
                  <Badge size="small" color="danger">
                    <Trans id="shipment.articles.table.archived.badge">Archived</Trans>
                  </Badge>
                ) : null}
                {showVerificationStatus && (
                  <ArticleReceivedVerificationStatusCell shipmentArticle={shipmentArticle} />
                )}
              </>
            }
          />
        </CardContent>
        <CardItems>
          <ArticleRequestCardItem article={article} organization={shipment.organization} />
          <ArticleWeightCardItem article={article} />
        </CardItems>
        {!!shipmentArticle.issue && <ArticleIssueCell issue={shipmentArticle.issue} />}
        {showActionsColumn && (
          <CardActions>
            {showVerificationActions && (
              <ArticleReceivedVerificationActionCell
                shipmentArticle={shipmentArticle}
                shipment={shipment}
                key={`${shipmentArticle.verified}`}
              />
            )}
            {showDeleteArticleAction && (
              <ArticleDeleteArticleActionCell
                articleId={article.id}
                shipmentId={shipment.id}
                isDisabled={shipment.articles.length === 1}
              />
            )}
            {showPrintArticleSheetActions && (
              <ArticlePrintArticleSheetActionCell article={article} />
            )}
            {showNextTaskColumn && <ArticleShipmentNextTaskCell article={article} />}
          </CardActions>
        )}
      </Card>
    </GridListItem>
  );
};
